/**
 * Utilidades para preservar el destino de un deep link a través del login.
 *
 * Cuando alguien abre un enlace profundo a una pantalla protegida —uno
 * compartido por correo o por chat, por ejemplo— sin tener sesión activa, el
 * `authGuard` lo desvía al login. Sin estas funciones ese destino se perdería y
 * el usuario acabaría siempre en `/app/home`, que es justo el caso más
 * probable: quien abre un enlace así suele hacerlo desde el móvil o tras días
 * sin entrar.
 *
 * El destino viaja por `sessionStorage` y no por un query param porque la
 * aplicación usa `withHashLocation()`: durante el flujo de redirección de MSAL
 * el hash se reescribe —y a veces se borra— y con él se iría el parámetro (ver
 * `specs/SPEC-AC-005-aislamiento-visor-publico.md`). `sessionStorage` sobrevive al
 * viaje a Azure AD y la vuelta porque es la misma pestaña y el mismo origen
 * (comprobado en navegador contra `login.microsoftonline.com`), y a diferencia
 * de `localStorage` no se comparte entre pestañas: dos deep links abiertos a la
 * vez no se pisan.
 *
 * Ver `specs/SPEC-AC-007-return-url-deep-links-autenticados.md`.
 */

/** Clave de `sessionStorage` donde se guarda el destino pendiente. */
export const CLAVE_RETURN_URL = 'sagd.returnUrl';

/**
 * Prefijos de ruta que nunca son un destino válido.
 *
 * `/auth` crearía un bucle de login y `/viewer` es el área pública, que no pasa
 * por el guard ni tiene sesión que recuperar.
 */
const PREFIJOS_EXCLUIDOS = ['/auth', '/viewer'];

/**
 * Rutas que no expresan ninguna intención: son las que el login usa por
 * defecto, así que guardarlas no aporta nada y sí estorba.
 *
 * Importa más de lo que parece. Al volver de Azure AD la respuesta llega a la
 * raíz del sitio en el hash (`/#code=…&state=…`), que no coincide con ninguna
 * ruta: el router cae en el comodín `**`, redirige a `/app` y **vuelve a
 * ejecutar el `authGuard`** antes de que MSAL haya procesado la respuesta. Sin
 * esta lista, esa segunda pasada guardaría `/app` encima del destino real y el
 * usuario acabaría en el inicio. Es el fallo que hacía inútil todo el mecanismo.
 */
const RUTAS_TRIVIALES = ['/', '/app', '/app/home'];

/** Tope defensivo de longitud, para no guardar basura en el almacenamiento. */
const LONGITUD_MAXIMA = 2048;

/**
 * Validez del destino guardado. Pasado ese tiempo se descarta: un login que
 * ocurre mucho después de haber abierto el enlace ya no tiene por qué
 * teletransportar al usuario a donde iba hace horas.
 */
const VIGENCIA_MS = 15 * 60 * 1000;

/**
 * Parámetros con los que Azure AD devuelve una respuesta de autenticación.
 * Su presencia significa que estamos a mitad del flujo de MSAL.
 */
const PARAMETROS_DE_RESPUESTA = [
  'code=',
  'id_token=',
  'access_token=',
  'error=',
];

/** Forma del valor persistido: la ruta y el momento en que se guardó. */
interface DestinoGuardado {
  r: string;
  t: number;
}

/**
 * Indica si la URL del navegador contiene una respuesta de autenticación.
 *
 * Mientras eso sea cierto no debe guardarse ningún destino: la navegación que
 * el router está resolviendo no la pidió el usuario, la provocó la vuelta desde
 * Azure AD.
 *
 * @param urlActual URL a inspeccionar. Por omisión, la del navegador; se puede
 *   pasar explícitamente para poder probar el caso sin tocar `location`
 * @returns true si la URL parece una respuesta de Azure AD
 */
export function hayRespuestaDeAutenticacionEnUrl(urlActual?: string): boolean {
  let candidato = urlActual;

  if (candidato === undefined) {
    if (typeof globalThis.location === 'undefined') {
      return false;
    }

    const { hash, search } = globalThis.location;
    candidato = `${hash}${search}`;
  }

  const texto = candidato;

  return PARAMETROS_DE_RESPUESTA.some((parametro) => texto.includes(parametro));
}

/**
 * Indica si una ruta puede usarse como destino tras el login.
 *
 * Solo se aceptan rutas internas de la aplicación. Se rechaza todo lo que
 * pudiera sacar al usuario del origen —URLs absolutas y relativas al
 * protocolo—, las áreas que no tienen sentido como destino y las rutas
 * triviales a las que el login llega por su cuenta.
 *
 * @param ruta Ruta de aplicación con barra inicial (por ejemplo `/app/document/abc`)
 * @returns true si la ruta es un destino interno aceptable
 */
export function esRutaDestinoValida(ruta: string | null | undefined): boolean {
  if (typeof ruta !== 'string' || ruta.length === 0) {
    return false;
  }

  if (ruta.length > LONGITUD_MAXIMA) {
    return false;
  }

  // Espacios y caracteres de control: una URL legítima los lleva codificados.
  // Se comprueba por código de carácter y no con una expresión regular para que
  // el rango no dependa de escapes que se degraden al reformatear el archivo.
  const tieneEspaciosOControl = Array.from(ruta).some((caracter) => {
    const codigo = caracter.codePointAt(0) ?? 0;
    return codigo <= 0x20 || codigo === 0x7f;
  });

  if (tieneEspaciosOControl) {
    return false;
  }

  // Debe ser una ruta interna, con una sola barra inicial.
  if (!ruta.startsWith('/')) {
    return false;
  }

  // `//host` y `/\host` son URLs relativas al protocolo: llevan a otro dominio.
  if (ruta.startsWith('//') || ruta.startsWith('/\\')) {
    return false;
  }

  // El esquema solo se busca en la ruta: un query param puede llevar una URL.
  const soloRuta = ruta.split('?')[0].split('#')[0];

  if (soloRuta.includes('://')) {
    return false;
  }

  if (RUTAS_TRIVIALES.includes(soloRuta)) {
    return false;
  }

  return !PREFIJOS_EXCLUIDOS.some(
    (prefijo) => soloRuta === prefijo || soloRuta.startsWith(`${prefijo}/`),
  );
}

/**
 * Guarda el destino al que el usuario quería llegar, si es válido.
 *
 * Se ignora en silencio cuando la ruta no es aceptable, cuando estamos a mitad
 * de una respuesta de Azure AD, o cuando el navegador impide usar
 * `sessionStorage` (modo privado, almacenamiento bloqueado): el login nunca
 * debe romperse por no poder recordar un destino.
 *
 * @param ruta Ruta de aplicación solicitada, tal como la expone `RouterStateSnapshot.url`
 * @param urlActual URL del navegador a inspeccionar. Por omisión la real; se
 *   puede pasar explícitamente en pruebas
 */
export function guardarRutaDestino(
  ruta: string | null | undefined,
  urlActual?: string,
): void {
  if (!esRutaDestinoValida(ruta)) {
    return;
  }

  // La vuelta desde Azure AD dispara navegaciones que el usuario no pidió.
  if (hayRespuestaDeAutenticacionEnUrl(urlActual)) {
    return;
  }

  const valor: DestinoGuardado = { r: ruta as string, t: Date.now() };

  try {
    sessionStorage.setItem(CLAVE_RETURN_URL, JSON.stringify(valor));
  } catch {
    // Sin almacenamiento disponible se pierde el destino, no la sesión.
  }
}

/**
 * Lee el destino pendiente **sin** descartarlo.
 *
 * Se usa para adjuntarlo a la petición de login como `state`, de modo que
 * también viaje por Azure AD y no dependa solo del almacenamiento local.
 *
 * @returns La ruta de destino, o `null` si no hay ninguna válida y vigente
 */
export function leerRutaDestino(): string | null {
  let crudo: string | null = null;

  try {
    crudo = sessionStorage.getItem(CLAVE_RETURN_URL);
  } catch {
    return null;
  }

  if (!crudo) {
    return null;
  }

  let valor: DestinoGuardado;

  try {
    valor = JSON.parse(crudo) as DestinoGuardado;
  } catch {
    return null;
  }

  if (!valor || typeof valor.r !== 'string' || typeof valor.t !== 'number') {
    return null;
  }

  if (Date.now() - valor.t > VIGENCIA_MS) {
    return null;
  }

  return esRutaDestinoValida(valor.r) ? valor.r : null;
}

/**
 * Recupera el destino pendiente y lo descarta.
 *
 * El valor se revalida al leerlo porque `sessionStorage` es editable desde la
 * consola del navegador: aunque lo hayamos escrito nosotros, al recuperarlo no
 * es confiable. Se consume una sola vez para que una navegación posterior no
 * lo reutilice.
 *
 * @returns La ruta de destino, o `null` si no hay ninguna válida y vigente
 */
export function consumirRutaDestino(): string | null {
  const destino = leerRutaDestino();

  limpiarRutaDestino();

  return destino;
}

/**
 * Descarta el destino pendiente.
 *
 * Se llama al cerrar sesión para que un enlace a medias no se dispare en la
 * sesión de otro usuario en el mismo navegador.
 */
export function limpiarRutaDestino(): void {
  try {
    sessionStorage.removeItem(CLAVE_RETURN_URL);
  } catch {
    // Nada que hacer: el destino simplemente no se podrá limpiar.
  }
}
